import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { Typography } from "antd";
import React, { useMemo } from "react";
import Grid from "../../../../atoms/Grid";
import { formatCurrency } from "../../../../libs/utils";
import { convertToNormalCase } from "../../../Contracts/PostDatedCheques/ReceiptMultiInvoiceAllocation/utils";
import { LinkedInvoice } from "./EditTerminationProcess.utils";

const GRID_HEADER_PX = 42;
const GRID_ROW_PX = 42;
const GRID_MAX_VISIBLE_ROWS = 8;
const GRID_MIN_HEIGHT_PX = GRID_HEADER_PX + GRID_ROW_PX * 5;

const defaultSettings = {
  sortable: true,
  resizable: true,
  minWidth: 100,
};

const centeredContainerStyle: React.CSSProperties = {
  minHeight: GRID_MIN_HEIGHT_PX,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 12px",
  textAlign: "center",
};

type LinkedInvoiceActionCellParams = ICellRendererParams<LinkedInvoice> & {
  linkedInvoiceIds?: string[];
  disabled?: boolean;
  onAddInvoice?: (invoice: LinkedInvoice) => void;
};

const LinkedInvoiceActionCell: React.FC<LinkedInvoiceActionCellParams> = (props) => {
  const record = props.data;
  if (!record?.pdcId) return <span />;

  const isLinked = props.linkedInvoiceIds?.includes(record.pdcId);

  if (isLinked) return <CheckOutlined style={{ color: "#22a06b", fontSize: 14 }} />;

  return (
    <button
      type="button"
      disabled={props.disabled}
      style={{
        border: "none",
        background: "transparent",
        cursor: props.disabled ? "not-allowed" : "pointer",
        padding: 6,
        margin: 0,
        lineHeight: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--blue-700)",
        opacity: props.disabled ? 0.5 : 1,
      }}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        props.onAddInvoice?.(record);
      }}
      aria-label={`Link ${record.invoiceNumber || "invoice"}`}
    >
      <PlusOutlined style={{ fontSize: 14 }} />
    </button>
  );
};

type LinkedInvoicesGridProps = {
  invoices?: LinkedInvoice[];
  disabled?: boolean;
  linkedInvoiceIds?: string[];
  onAddInvoice: (invoice: LinkedInvoice) => void;
};

const getStatusPillStyle = (status?: string): React.CSSProperties => {
  const normalizedStatus = String(status || "").toLowerCase();

  const baseStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 50,
    height: 20,
    padding: "4px 8px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
  };

  if (normalizedStatus === "paid" || normalizedStatus === "fully_paid" || normalizedStatus === "fully paid") {
    return {
      ...baseStyle,
      color: "#168a55",
      background: "#e2f4ec",
    };
  }

  return {
    ...baseStyle,
    color: "#a65f1b",
    background: "#fdebd8",
  };
};

const getChequeStatusLabel = (status?: string) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "paid" || normalizedStatus === "fully_paid" || normalizedStatus === "fully paid") return "Paid";
  return "Unpaid";
};

const getChequeStatusValue = (invoice?: LinkedInvoice) => invoice?.chequeStatus || invoice?.status;

const isChequeStatusPaid = (invoice?: LinkedInvoice) => {
  const normalizedStatus = String(getChequeStatusValue(invoice) || "").toLowerCase();
  return normalizedStatus === "paid" || normalizedStatus === "fully_paid" || normalizedStatus === "fully paid";
};

const getChequeStatusPillStyle = (invoice?: LinkedInvoice) => {
  return getStatusPillStyle(isChequeStatusPaid(invoice) ? "paid" : "unpaid");
};

export const LinkedInvoicesGrid: React.FC<LinkedInvoicesGridProps> = ({
  invoices = [],
  disabled,
  linkedInvoiceIds = [],
  onAddInvoice,
}) => {
  const gridHeight = useMemo(() => {
    const rowCount = Math.max(invoices.length, 1);
    const visibleRows = Math.min(rowCount, GRID_MAX_VISIBLE_ROWS);
    return Math.max(GRID_HEADER_PX + visibleRows * GRID_ROW_PX, GRID_MIN_HEIGHT_PX);
  }, [invoices.length]);

  const actionCellParams = useMemo(
    () => ({
      linkedInvoiceIds,
      disabled,
      onAddInvoice,
    }),
    [linkedInvoiceIds, disabled, onAddInvoice],
  );

  const columns = useMemo<ColDef<LinkedInvoice>[]>(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        width: 130,
        valueFormatter: ({ value }) => value || "--",
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.2,
        minWidth: 160,
        valueFormatter: ({ value }) => convertToNormalCase(value) || "--",
      },
      {
        field: "totalAmount",
        headerName: "Amount",
        width: 170,
        type: "rightAligned",
        valueFormatter: ({ value }) => formatCurrency(value),
      },
      {
        field: "chequeStatus",
        headerName: "Status",
        width: 140,
        cellRenderer: ({ data }: ICellRendererParams<LinkedInvoice>) => (
          <span style={getChequeStatusPillStyle(data)}>
            {getChequeStatusLabel(getChequeStatusValue(data))}
          </span>
        ),
      },
      {
        colId: "action",
        headerName: "Action",
        width: 48,
        maxWidth: 48,
        minWidth: 48,
        sortable: false,
        resizable: false,
        suppressMenu: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        cellRenderer: LinkedInvoiceActionCell,
        cellRendererParams: actionCellParams,
      },
    ],
    [actionCellParams],
  );

  if (!invoices.length)
    return (
      <div style={centeredContainerStyle}>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          No invoices found.
        </Typography.Paragraph>
      </div>
    );

  return (
   <div className="linked-invoices-grid-container">
     <Grid   
      columns={columns}
      data={invoices}
      defaultSettings={defaultSettings}
      pagination={false}
      height={gridHeight}
      headerHeight={GRID_HEADER_PX}
      rowHeight={GRID_ROW_PX}
      refreshAction={linkedInvoiceIds.join(",")}
    />
   </div>
  );
};
